import React, { useState } from 'react';
import { ModalInfo } from '../../../../../theme/ui/Atoms/Modal';
import { CircularProgress, Stack, Typography } from '@mui/material';
import Button from '../../../../../theme/ui/Atoms/Button';
import { useTranslation } from 'react-i18next';
import FlexeserveDeviceAPI from '../../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import CommanderAirConnectAPI from '../../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI';
import { ComposableDevicesStoreInfoResponse } from '../../../../../service/store/hooks/useDevicesWithComposableDevice';
import { ComposableDevicesManagerStoreInfoResponse } from '../../../../../service/device/hooks/useDevicesManager';
import useErrorMessage from '../../../../../shared/hooks/useErrorMessage';
import useMessage from '../../../../../shared/hooks/useMessage';

interface RemoveDeviceModalProps {
  displayConfirmModal: boolean;
  setDisplayConfirmModal: (display: boolean) => void;
  device:
    | ComposableDevicesStoreInfoResponse
    | ComposableDevicesManagerStoreInfoResponse;
  canRemoveNode?: boolean;
}
export const RemoveDeviceModal = (props: RemoveDeviceModalProps) => {
  const { displayConfirmModal, setDisplayConfirmModal, device, canRemoveNode } =
    props;
  const { t } = useTranslation();
  const [deleteFlexeserveDevice, { error: errorDeletingFlexeserveDevice }] =
    FlexeserveDeviceAPI.useDeleteFlexeserveDeviceMutation();
  const [removeNode, { isLoading: isRemovingNode, error: errorRemovingNode }] =
    CommanderAirConnectAPI.useRemoveNodeMutation();

  const [isRemoving, setIsRemoving] = useState(false);

  const showMessage = useMessage();
  const showErrorMessage = useErrorMessage();

  const onDeleteClick = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsRemoving(true);

    const nodeInfo = device.composableDevice?.labels;
    const node = {
      macAddress: device.mac ?? '',
      bitmask: Number(nodeInfo?.['node.bitmask'] ?? 0),
      nodeAddress: Number(nodeInfo?.['node.nodeAddress'] ?? 0),
      nodeNumber: Number(nodeInfo?.['node.nodeNumber'] ?? 0),
      nodeReachable: nodeInfo?.['node.nodeReachable'] === 'true',
      nodeType: Number(nodeInfo?.['node.nodeType'] ?? 0),
      numberOfControllers: Number(nodeInfo?.['node.numberOfControllers'] ?? 0),
      numberOfControllersInstalled: Number(
        nodeInfo?.['node.numberOfControllersInstalled'] ?? 0
      ),
    };

    try {
      if (canRemoveNode) {
        await removeNode({
          commanderId: device.commanderId ?? '',
          node,
        }).unwrap();
      }
      await deleteFlexeserveDevice(device.uuid ?? '').unwrap();

      showMessage(t('device.remove_successful_message'));
      setIsRemoving(false);
      setDisplayConfirmModal(false);
    } catch (error) {
      setIsRemoving(false);
      showErrorMessage(
        t('device.remove_error_message', {
          message: errorDeletingFlexeserveDevice || errorRemovingNode,
        })
      );
    }
  };

  return (
    <ModalInfo
      open={displayConfirmModal}
      PaperProps={{
        sx: {
          padding: '2rem 4rem',
          width: '70vw',
        },
      }}
      description={
        <Stack>
          <Typography variant="body1" component={'p'} p="0.5rem" align="center">
            {t('device.remove_flexeserve_device_dialog_text', {
              deviceName: device.unitName,
            })}
          </Typography>
        </Stack>
      }
      title={
        <Typography fontSize="1.5rem" align="center">
          {t('device.remove_dialog_title')}
        </Typography>
      }
      buttonActions={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-evenly"
          width={'100%'}
        >
          <Button
            autoFocus
            variant="outlined"
            rounded
            disabled={isRemoving || isRemovingNode}
            onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
              ev.preventDefault();
              setDisplayConfirmModal(false);
            }}
            sx={{ flex: '1 1 0px' }}
          >
            {t('button.cancel')}
          </Button>

          <Button
            autoFocus
            variant="contained-gray"
            rounded
            disabled={isRemoving || isRemovingNode}
            onClick={onDeleteClick}
            sx={{ flex: '1 1 0px' }}
          >
            {isRemoving || isRemovingNode ? (
              <CircularProgress size="1rem" />
            ) : (
              t('button.delete')
            )}
          </Button>
        </Stack>
      }
    />
  );
};
